import React, { useEffect, useMemo } from 'react';
import { Alert } from 'reactstrap';
import { Form, Formik } from 'formik';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Spinner } from '~components';
import { Fields } from '~components/form-fields';

import { CarePlan } from '~clients/models';

import { culturalSafetyFields, culturalSafetySchema } from '~clients/forms/care-plans/culturalSafety';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import {
  useCreateCarePlanCulturalSafetyMutation,
  useGetCarePlanCulturalSafetyQuery,
  useUpdateCarePlanCulturalSafetyMutation,
} from '~clients/actions/carePlans';

interface ComponentProps {
  plan: CarePlan;
  /** Whether the Cultural Safety tab is the visible one (tabs stay mounted while hidden). */
  isActive?: boolean;
}

// The index signature satisfies the Fields component's values contract.
interface CulturalSafetyValues {
  [key: string]: string | number | boolean | object | null;
  background_notes: string;
  trauma_notes: string;
  is_interpreter_required: boolean;
}

const definitions = Object.entries(culturalSafetyFields).map(([key, props]) => ({ key, ...props }));

/**
 * Cultural Safety tab — a single-save FormFields form backing the one
 * cultural-safety record per care plan (sah_ongoing only). While no record has
 * its required cultural-background field populated, an incomplete alert warns
 * that the section blocks activation; the alert clears once the record is saved.
 * First save creates the record (POST); subsequent saves update it (PATCH). Both
 * invalidate the shared cache tag, so the sidebar activation checklist recomputes.
 */
const CulturalSafetyTab: React.FC<ComponentProps> = ({ plan, isActive }) => {
  const { data: record, isLoading, error } = useGetCarePlanCulturalSafetyQuery({ planId: plan.id });
  const [createCulturalSafety, { isLoading: isCreating, error: createError, reset: resetCreate }] =
    useCreateCarePlanCulturalSafetyMutation();
  const [updateCulturalSafety, { isLoading: isUpdating, error: updateError, reset: resetUpdate }] =
    useUpdateCarePlanCulturalSafetyMutation();
  const { showToast } = useToast();

  // Tabs stay mounted, so clear any stale save-error banner when the user navigates off this tab.
  useEffect(() => {
    if (isActive === false) {
      resetCreate?.();
      resetUpdate?.();
    }
  }, [isActive, resetCreate, resetUpdate]);

  const { invalidFields, validate } = useFieldValidationHighlights(culturalSafetySchema);

  const isSaving = isCreating || isUpdating;
  const saveError = createError ?? updateError;
  const isIncomplete = !record?.background_notes;

  const initialValues: CulturalSafetyValues = useMemo(
    () => ({
      background_notes: record?.background_notes ?? '',
      trauma_notes: record?.trauma_notes ?? '',
      is_interpreter_required: record?.is_interpreter_required ?? false,
    }),
    [record],
  );

  const onSubmit = async (values: CulturalSafetyValues) => {
    const body = {
      background_notes: values.background_notes.trim(),
      trauma_notes: values.trauma_notes?.trim() || null,
      is_interpreter_required: values.is_interpreter_required,
    };
    try {
      if (record) {
        await updateCulturalSafety({ id: record.id, ...body }).unwrap();
      } else {
        await createCulturalSafety({ planId: plan.id, ...body }).unwrap();
      }
      showToast('success', 'Cultural safety saved');
    } catch {
      // Surfaced through saveError below.
    }
  };

  if (isLoading) {
    return (
      <div className="care-plan-section-loading">
        <Spinner loading />
      </div>
    );
  }

  if (error !== undefined) {
    return <ErrorResponse dataCy="care-plan-cultural-safety-error" message={getErrorMessage(error)} />;
  }

  return (
    <div className="section-card-container" data-cy="care-plan-cultural-safety">
      {isIncomplete && (
        <Alert color="warning" className="care-plan-alert" data-cy="care-plan-cultural-safety-incomplete">
          Cultural safety section is incomplete. Required before care plan can be activated.
        </Alert>
      )}
      {saveError !== undefined && (
        <ErrorResponse dataCy="care-plan-cultural-safety-save-error" message={getErrorMessage(saveError)} />
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={culturalSafetySchema}
        validate={validate}
        validateOnMount={false}
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, setFieldTouched, touched, dirty }) => (
          // care-plan-form-body normalises the FormFields input padding (as the modals do).
          <Form className="care-plan-form-body">
            <Fields
              definitions={definitions}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              touched={touched}
              invalidFields={invalidFields}
            />
            <div className="care-plan-section-actions">
              <Button
                type="submit"
                size="sm"
                color="success"
                disabled={!dirty}
                loading={isSaving}
                dataCy="care-plan-cultural-safety-save"
              >
                Save
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CulturalSafetyTab;
